import type { OperationItem } from '@ops/entities/operation-item';
import type { DashboardRowView } from '../lib/row-view';
import { assignableOperatorType } from '@ops/entities/operation-item';
import { OperationCardOperatorPicker } from '@ops/widgets/operations-layout/ui/operation-card-operator-picker';
import { ATTENTION_RED_FLAGGED } from '@travel-planner/shared/schemas/operations';
import { Flag } from 'lucide-react';
import { joinCss } from '@/shared/lib/css-utils';
import { Avatar, Checkbox, TableCell, TableRow } from '@/shared/ui';
import {
  DASHBOARD_COLUMNS,
  DASHBOARD_ROW_MIN,
  SERVICE_BASIS,
} from '../lib/columns';
import { DashboardRowMenu } from './dashboard-row-menu';
import css from './items-table.module.scss';

type DashboardRowProps = {
  item: OperationItem;
  row: DashboardRowView;
  selected: boolean;
  /** The status's own org colour (per-status), like the /operations dot. */
  statusColor: string | null;
  onToggleSelect: (id: string) => void;
  onOpenItinerary: (itineraryId: string) => void;
  onOpenInOperations: (itineraryId: string) => void;
  onToggleFlag: (item: OperationItem) => void;
};

const assignedOperatorIds = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw
        .map((op) => (op as { id?: string } | null)?.id)
        .filter((id): id is string => Boolean(id))
    : [];

export const DashboardRow = ({
  item,
  row,
  selected,
  statusColor,
  onToggleSelect,
  onOpenItinerary,
  onOpenInOperations,
  onToggleFlag,
}: DashboardRowProps) => {
  const operatorType = assignableOperatorType(item);
  const isFlagged = item.attentionCategory === ATTENTION_RED_FLAGGED;
  return (
    <TableRow
      className={joinCss(selected && css.rowIsSelected)}
      data-item-id={row.id}
      minWidth={DASHBOARD_ROW_MIN}
    >
      <TableCell width={DASHBOARD_COLUMNS.select}>
        <Checkbox
          aria-label={`Select ${row.service}`}
          checked={selected}
          onChange={() => onToggleSelect(row.id)}
        />
      </TableCell>

      <TableCell className={css.cellStack} width={DASHBOARD_COLUMNS.when}>
        <span className={css.primary}>{row.dateLabel}</span>
        <span className={css.secondary}>{row.timeLabel}</span>
      </TableCell>

      <TableCell basis={SERVICE_BASIS} className={css.cellStack} flexGrow={1}>
        <span className={css.serviceName}>{row.service}</span>
        <span className={css.secondary}>{row.category}</span>
      </TableCell>

      <TableCell className={css.cellStack} width={DASHBOARD_COLUMNS.itinerary}>
        {row.itineraryId ? (
          <button
            className={css.ref}
            onClick={() => onOpenItinerary(row.itineraryId as string)}
            type="button"
          >
            {row.itineraryRef}
          </button>
        ) : (
          <span className={css.primary}>{row.itineraryRef}</span>
        )}
        <span className={css.secondary}>{row.traveller}</span>
      </TableCell>

      <TableCell width={DASHBOARD_COLUMNS.location}>
        <span className={css.location}>{row.location}</span>
      </TableCell>

      <TableCell width={DASHBOARD_COLUMNS.owner}>
        <span className={css.secondary}>{row.owner}</span>
      </TableCell>

      <TableCell width={DASHBOARD_COLUMNS.operator}>
        <div className={css.who}>
          {row.operators.length > 0 ? (
            <>
              <Avatar initials={row.operators[0].initials} size="sm" />
              <span className={css.operatorName}>{row.operators[0].name}</span>
            </>
          ) : operatorType ? (
            <OperationCardOperatorPicker
              assignedIds={assignedOperatorIds(item.tourOperators)}
              endTime={item.endTime}
              itemId={item.id ?? ''}
              operatorType={operatorType}
              startTime={item.startTime}
            />
          ) : (
            <span className={css.secondary}>—</span>
          )}
        </div>
      </TableCell>

      <TableCell width={DASHBOARD_COLUMNS.status}>
        {row.statusTitle ? (
          <span className={css.status}>
            <span
              className={css.statusDot}
              style={{ backgroundColor: statusColor ?? 'var(--color-muted)' }}
            />
            <span className={css.statusLabel}>{row.statusTitle}</span>
          </span>
        ) : (
          <span className={css.secondary}>—</span>
        )}
      </TableCell>

      <TableCell align="right" width={DASHBOARD_COLUMNS.actions}>
        <div className={css.actions}>
          <button
            aria-label={isFlagged ? 'Unflag' : 'Flag'}
            className={joinCss(
              css.actionBtn,
              isFlagged && css.actionBtnIsFlagged
            )}
            onClick={() => onToggleFlag(item)}
            type="button"
          >
            <Flag size={15} />
          </button>
          <DashboardRowMenu
            onOpenInOperations={
              row.itineraryId
                ? () => onOpenInOperations(row.itineraryId as string)
                : undefined
            }
            onOpenItinerary={
              row.itineraryId
                ? () => onOpenItinerary(row.itineraryId as string)
                : undefined
            }
          />
        </div>
      </TableCell>
    </TableRow>
  );
};
